import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, of } from 'rxjs';
import { formatDate } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '../../../ui';
import { ContractsService } from '../contracts.service';
import { AuthService } from '../../../core/auth/auth.service';
import { Invoice, InvoiceService } from '../../invoice/invoice.service';
import {
  contractChangeDateOutcome,
  maxInvoiceEndDate,
} from '../../../shared/utils/contract-change-date.util';
import { minDateValidator } from '../../../shared/validators/min-date.validator';
import {
  HLM_DIALOG_SHELL,
  HLM_FORM_FIELD,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSpinnerComponent,
  HlmAlertComponent,
} from '../../../ui';

export interface ChangeToBasicDialogData {
  contractId: number;
  /** Needed for the invoiced-period rule — same rule as Change Plan / Send-to-Basic. */
  customerId: number;
  invoiceDay: number;
  contractStartDate: string;
  reactivatedStartDate?: string | null;
  planLevel?: number | null;
  currentPlanName: string;
  /** Current contract's price list — the Basic plan is resolved within it. */
  priceListId: number;
  priceListName: string;
  currency: string;
}

/**
 * EB-4005: one-click downgrade of the (main) contract to the "Basic" plan of its
 * current price list, avoiding the cancel-first-then-rebook detour. Basic is the
 * **level-5** plan in the same price list, so the plan change cascades to all contracts
 * just like a normal Change Plan.
 *
 * There is deliberately **no** name-based fallback: "Basic" is level 1 in some price
 * lists and level 5 is called "Bare" in others, so matching by name could pick the wrong
 * plan. This doc said "fallback: name 'Basic'" while the code stated and did the opposite
 * (PR #42 review, roundup #24).
 */
@Component({
  selector: 'cmt-change-to-basic-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL, ...HLM_FORM_FIELD,
    HlmBtnDirective, HlmIconComponent, HlmInputDirective, HlmSpinnerComponent, HlmAlertComponent,
  ],
  template: `
    <hlm-dialog-surface size="sm">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="destructive">
          <hlm-icon name="lucideArrowDown" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>Change to Basic</h2>
          <p hlmDialogDescription>
            Contract <span class="font-mono-feat">{{ data.contractId }}</span> ·
            price list {{ data.priceListName }}
          </p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        @if (loading()) {
          <div class="flex items-center gap-3 py-8 text-sm text-muted-foreground">
            <hlm-spinner size="18" />
            <span>Loading Basic plan...</span>
          </div>
        } @else if (errorMsg()) {
          <hlm-alert tone="error">
            <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
            <span>{{ errorMsg() }}</span>
          </hlm-alert>
        } @else {
          <form [formGroup]="form" class="flex flex-col gap-4">
            <p class="text-sm text-foreground">
              Downgrade from <strong>{{ data.currentPlanName }}</strong> to
              <strong>{{ basicPlanName() }}</strong>
              (<span class="font-mono-feat">0 {{ data.currency }}/mo</span>).
              All contracts will be updated.
            </p>
            <hlm-form-field>
              <label hlmLabel for="changeDate">Change date</label>
              <input id="changeDate" hlmInput type="date" formControlName="changeDate"
                     [min]="minDateStr()" />
              <p hlmFieldHint>{{ changeDateHint() }}</p>
              @if (form.controls.changeDate.hasError('minDate')) {
                <p hlmFieldError>The downgrade cannot take effect before {{ minDateDisplay() }}.</p>
              }
            </hlm-form-field>
          </form>
        }
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()" [disabled]="submitting()">
          Cancel
        </button>
        <button hlmBtn variant="destructive" (click)="submit()" [disabled]="!canSubmit()">
          @if (submitting()) {
            <hlm-spinner size="16" /> Changing...
          } @else {
            <hlm-icon name="lucideArrowDown" size="0.9rem" />
            Change to Basic
          }
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class ChangeToBasicDialogComponent implements OnInit {
  readonly data = inject<ChangeToBasicDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<ChangeToBasicDialogComponent>);
  private readonly contractsService = inject(ContractsService);
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly errorMsg = signal<string | null>(null);
  readonly submitting = signal(false);
  readonly basicPlanName = signal('Basic');
  private basicPlanId: number | null = null;

  /**
   * Downgrading to Basic is a plan change, so it follows the same floor as Change
   * Plan and the Send-to-Basic mode of the cancel dialog: contract-aware base rule,
   * tightened past the last invoiced period once the invoices load. This dialog used
   * to sit on `today` / tomorrow, so the same action behaved differently depending on
   * which entry point the operator picked (EB-4170 review H5).
   */
  private readonly baseOutcome = contractChangeDateOutcome({
    contractStartDate: this.data.contractStartDate,
    reactivatedStartDate: this.data.reactivatedStartDate,
    planLevel: this.data.planLevel,
    invoiceDay: this.data.invoiceDay,
  });
  readonly minDateStr = signal(this.baseOutcome.minDateStr);
  readonly changeDateHint = signal(this.baseOutcome.hint);
  readonly minDateDisplay = computed(() => formatDate(this.minDateStr(), 'dd.MM.yyyy', 'en'));

  readonly form: FormGroup = this.fb.group({
    changeDate: [
      this.baseOutcome.prefill,
      [Validators.required, minDateValidator(() => this.minDateStr())],
    ],
  });

  ngOnInit(): void {
    this.applyInvoicedPeriodRule();

    this.contractsService.getAvailablePlans(this.data.contractId).subscribe({
      next: (plans) => {
        const inPriceList = plans.filter((p) => p.priceList.id === this.data.priceListId);
        // Basic = the level-5 plan of this price list (old-CMT parity, contractRevisions.js).
        // No name-based fallback: "Basic" is level 1 in some lists and level 5 is "Bare" in
        // others, so matching by name could pick the wrong plan.
        const basic = inPriceList.find((p) => p.level === 5);
        if (!basic) {
          this.errorMsg.set('No Basic plan available in this price list.');
        } else {
          this.basicPlanId = basic.id;
          this.basicPlanName.set(basic.name ?? 'Basic');
        }
        this.loading.set(false);
      },
      error: () => {
        this.errorMsg.set('Failed to load available plans');
        this.loading.set(false);
      },
    });
  }

  canSubmit(): boolean {
    return this.form.valid && !this.loading() && !this.submitting() && !this.errorMsg() && this.basicPlanId !== null;
  }

  submit(): void {
    if (!this.canSubmit()) return;
    this.submitting.set(true);

    const startDate = this.form.value.changeDate;

    this.contractsService.modifyContract(
      this.data.contractId,
      [{ type: 'REPLACE', attribute: 'plan', value: this.basicPlanId! }],
      { startDate, employeeId: String(this.authService.currentUserId) },
    ).subscribe({
      next: () => {
        this.toast.open('Changed to Basic. All contracts will be updated.', 'OK', { duration: 4000 });
        this.dialogRef.close(true);
      },
      error: () => {
        this.toast.open('Failed to change to Basic', 'OK', { duration: 6000 });
        this.submitting.set(false);
      },
    });
  }

  /** See {@link baseOutcome} — tightens the floor once the customer's invoices are known. */
  private applyInvoicedPeriodRule(): void {
    this.invoiceService.getCurrentInvoices(this.data.customerId).pipe(
      map((invoices) => ({ invoices, failed: false })),
      catchError(() => of({ invoices: [] as Invoice[], failed: true })),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ invoices, failed }) => {
      const outcome = contractChangeDateOutcome({
        contractStartDate: this.data.contractStartDate,
        reactivatedStartDate: this.data.reactivatedStartDate,
        planLevel: this.data.planLevel,
        invoiceDay: this.data.invoiceDay,
        maxInvoiceEnd: maxInvoiceEndDate(invoices),
        invoiceLookupFailed: failed,
      });

      this.minDateStr.set(outcome.minDateStr);
      this.changeDateHint.set(outcome.hint);

      const control = this.form.controls['changeDate'];
      if (!control.dirty) control.setValue(outcome.prefill);
      control.updateValueAndValidity();
    });
  }

}
